<ng-container *transloco="let t">
  @let shippingCostResponseValue = shippingCostResponse();

  @if (calculatingShippingCost()) {
    <!-- Shipping calculation in progress -->
    <pxw-alert type="neutral" size="sm" display="inline">
      <pxw-icon icon="loading" [spin]="true" />
      {{ t('shippingCostResponse.calculating') }}
    </pxw-alert>
    <pxw-form-validation
      name="shippingCostCalculation"
      style="margin-top: 0.3em"
      [displayErrors]="isFormSubmitted()"
      [ngModel]="false"
      [requiredTrue]="true"
      [messages]="{ required: t('shippingCostResponse.calculatingError') }"
    />
  } @else if (shippingCostResponseValue) {
    @let selectedShippingOption = shippingCostResponseValue.shippingOption;

    @if (selectedShippingOption.type === typesOfShippings.DELIVERY) {
      @if (shippingCostResponseValue.deliveryCost && shippingCostResponseValue.deliveryCost > 0) {
        <!-- Display delivery cost -->
        <pxw-alert type="warning" size="sm" display="inline-block">
          <pxw-icon icon="shipping" #customIcon />
          {{ t('shippingCostResponse.costLabel') }}:
          <strong>{{ shippingCostResponseValue.deliveryCost | currencyFormat }}</strong>

          @if (selectedShippingOption.deliveryType === deliveryTypes.FIXED) {
            <ng-template #alertDetails>
              <span class="shipping-cost-helper">
                *{{ t('shippingCostResponse.costHelper') }}
              </span>
            </ng-template>
          }
        </pxw-alert>

        @if (shippingCostResponseValue.minimumPurchaseForFreeShipping) {
          <!-- Can unlock free shipping -->
          <pxw-alert
            type="info"
            appareance="bare"
            size="xs"
            display="inline-block"
            style="margin-top: 0.3em"
          >
            <pxw-icon icon="price-discount" #customIcon />
            *
            {{
              t('shippingCostResponse.minimumPurchaseForFreeShipping', {
                minimumPurchaseForFreeShipping:
                  shippingCostResponseValue.minimumPurchaseForFreeShipping | currencyFormat,
              })
            }}
          </pxw-alert>
        }
      } @else if (shippingCostResponseValue.deliveryCostToBeDefined) {
        <!-- Delivery cost to be defined -->
        <ng-container
          *ngTemplateOutlet="
            canContinueWithConfirmation;
            context: { message: t('shippingCostResponse.toBeDefined') }
          "
        />
      } @else if (shippingCostResponseValue.freeShippingGrantedAmount) {
        <!-- Free delivery granted -->
        <div style="max-width: 100%;">
          <pxw-alert type="success" size="sm" display="inline-block" style="width: 100%;">
            <pxw-icon icon="price-discount" #customIcon />
            {{ t('shippingCostResponse.freeShippingGranted') }}
          </pxw-alert>
        </div>
      } @else if (shippingCostResponseValue.minimumPurchaseErrorAmount) {
        <!-- Minimum purchase error -->
        <ng-container
          *ngTemplateOutlet="
            shippingNotAllowed;
            context: {
              message: t('shippingCostResponse.minimumPurchaseError', {
                minimumPurchase:
                  shippingCostResponseValue.minimumPurchaseErrorAmount | currencyFormat,
              }),
            }
          "
        />
      } @else if (shippingCostResponseValue.shippingEstimate?.error) {
        <!-- Shipping estimate error -->
        <ng-container
          *ngTemplateOutlet="
            canContinueWithConfirmation;
            context: { message: shippingCostResponseValue.shippingEstimate?.error }
          "
        />
      } @else if (shippingCostResponseValue.outOfDeliveryZone) {
        <!-- Out of delivery zone -->
        @if (selectedShippingOption.outOfDeliveryZoneShippingAllowed) {
          <!-- Allowed with confirmation -->
          <ng-container
            *ngTemplateOutlet="
              canContinueWithConfirmation;
              context: {
                message: t('shippingCostResponse.outOfDeliveryZoneAlert'),
              }
            "
          />
        } @else {
          <!-- Shipping disabled -->
          <ng-container
            *ngTemplateOutlet="
              shippingNotAllowed;
              context: {
                message: t('shippingCostResponse.outOfDeliveryZoneForbiddenAlert'),
              }
            "
          />
        }
      }
    } @else if (selectedShippingOption.type === typesOfShippings.PARCEL) {
      @if (shippingCostResponseValue.freeShippingGrantedAmount) {
        <!-- Free parcel shipping granted -->
        <div style="max-width: 100%;">
          <pxw-alert type="success" size="sm" display="inline-block" style="width: 100%;">
            <pxw-icon icon="price-discount" #customIcon />
            {{ t('shippingCostResponse.freeShippingGranted') }}
          </pxw-alert>
        </div>
      } @else if (shippingCostResponseValue.minimumPurchaseErrorAmount) {
        <!-- Minimum purchase error for parcel -->
        <ng-container
          *ngTemplateOutlet="
            shippingNotAllowed;
            context: {
              message: t('shippingCostResponse.minimumPurchaseError', {
                minimumPurchase:
                  shippingCostResponseValue.minimumPurchaseErrorAmount | currencyFormat,
              }),
            }
          "
        />
      } @else if (shippingCostResponseValue.postalCodeRequired) {
        <!-- Postal code required for parcel shipping -->
        <ng-container
          *ngTemplateOutlet="
            shippingNotAllowed;
            context: {
              message: t('shippingCostResponse.postalCodeRequiredForParcel'),
            }
          "
        />
      } @else if (shippingCostResponseValue.deliveryCostToBeDefined) {
        <!-- Parcel delivery cost to be defined -->
        <ng-container
          *ngTemplateOutlet="
            canContinueWithConfirmation;
            context: { message: 'No pudimos calcular el costo de envío en este momento.' }
          "
        />
      } @else if (shippingCostResponseValue.availableParcelOptions && shippingCostResponseValue.availableParcelOptions.length > 1) {
        <!-- Multiple parcel options available - show selector always -->
        <pxw-shipping-options-selector
          [availableOptions]="shippingCostResponseValue.availableParcelOptions"
          [selectedOption]="selectedParcelOption()"
          [isFormSubmitted]="isFormSubmitted()"
          (selectedOptionChange)="onParcelOptionSelected($event)"
        />

        @if (shippingCostResponseValue.minimumPurchaseForFreeShipping) {
          <!-- Can unlock free parcel shipping -->
          <pxw-alert
            type="info"
            appareance="bare"
            size="xs"
            display="inline-block"
            style="margin-top: 0.3em"
          >
            <pxw-icon icon="price-discount" #customIcon />
            *
            {{
              t('shippingCostResponse.minimumPurchaseForFreeShipping', {
                minimumPurchaseForFreeShipping:
                  shippingCostResponseValue.minimumPurchaseForFreeShipping | currencyFormat,
              })
            }}
          </pxw-alert>
        }
      } @else if (
        shippingCostResponseValue.deliveryCost && shippingCostResponseValue.deliveryCost > 0
      ) {
        <!-- Single parcel option selected or auto-selected - show cost alert -->
        <pxw-alert type="warning" size="sm" display="inline-block">
          <pxw-icon icon="shipping" #customIcon />
          {{ t('shippingCostResponse.costLabel') }}:
          <strong>{{ shippingCostResponseValue.deliveryCost | currencyFormat }}</strong>

          <ng-template #alertDetails>
            <div class="parcel-shipping-details">
              @if (shippingCostResponseValue.shippingEstimate?.deliveryMode) {
                <div class="parcel-service">
                  <strong>{{ shippingCostResponseValue.shippingEstimate?.deliveryMode }}</strong>
                </div>
              }
              @if (shippingCostResponseValue.shippingEstimate?.deliveryTime) {
                <div class="parcel-delivery-time">
                  {{
                    formatDeliveryDate(shippingCostResponseValue.shippingEstimate?.deliveryTime!)
                  }}
                </div>
              }
              <div class="parcel-provider">
                Envío por
                {{
                  shippingCostResponseValue.shippingEstimate?.providerName === 'correoargentino'
                    ? 'Correo Argentino'
                    : shippingCostResponseValue.shippingEstimate?.providerName
                }}
              </div>
            </div>
          </ng-template>
        </pxw-alert>

        @if (shippingCostResponseValue.minimumPurchaseForFreeShipping) {
          <!-- Can unlock free parcel shipping -->
          <pxw-alert
            type="info"
            appareance="bare"
            size="xs"
            display="inline-block"
            style="margin-top: 0.3em"
          >
            <pxw-icon icon="price-discount" #customIcon />
            *
            {{
              t('shippingCostResponse.minimumPurchaseForFreeShipping', {
                minimumPurchaseForFreeShipping:
                  shippingCostResponseValue.minimumPurchaseForFreeShipping | currencyFormat,
              })
            }}
          </pxw-alert>
        }
      } @else if (shippingCostResponseValue.shippingEstimate?.error) {
        <!-- Parcel shipping estimate error -->
        <ng-container
          *ngTemplateOutlet="
            canContinueWithConfirmation;
            context: { message: 'No pudimos calcular el costo de envío en este momento.' }
          "
        />
      }
    }
  }

  <ng-template #canContinueWithConfirmation let-message="message">
    <pxw-alert type="warning" size="sm" display="inline-block">
      {{ message }}

      <ng-template #alertDetails>
        <pxw-input-checkbox
          name="outOfDeliveryZoneConfirmation"
          [legend]="t('shippingCostResponse.outOfDeliveryZoneConfirmLabel')"
          [(ngModel)]="outOfDeliveryZoneConfirmed"
        />
      </ng-template>
    </pxw-alert>
    <pxw-form-validation
      name="outOfDeliveryZoneConfirmationValidation"
      style="margin-top: 0.3em"
      [displayErrors]="isFormSubmitted()"
      [ngModel]="outOfDeliveryZoneConfirmed()"
      [requiredTrue]="true"
    />
  </ng-template>
  <ng-template #shippingNotAllowed let-message="message">
    <pxw-form-validation
      name="outOfDeliveryZoneShippingNotAllowed"
      [displayErrors]="true"
      [ngModel]="false"
      [requiredTrue]="true"
      [messages]="{
        required: message,
      }"
    />
  </ng-template>
</ng-container>
